"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ViewObserver = void 0;
class ViewObserver {
    constructor(options) {
        this.observe = (element, callback) => {
            this.elements.set(element, {
                callback,
                visible: false,
                entry: element,
            });
            this.observer.observe(element);
        };
        this.unobserve = (element) => {
            if (!this.elements.get(element)) {
                return;
            }
            this.observer.unobserve(element);
            this.elements.delete(element);
        };
        /**
         * callback for Intersection Observer
         *
         * Loop through all the observed elements and check if visible
         *
         * @param  {Array} [entries] array of [IntersectionObserverEntry],
         */
        this._watchElements = (entries = []) => {
            /**
             * If rootBounds does not exist, it will default to the height and width of the viewport
             */
            const containerHeight = window.innerHeight || document.documentElement.clientHeight;
            entries
                .filter((entry) => entry.isIntersecting)
                .forEach((entry) => {
                const node = entry.target;
                const element = this.elements.get(node);
                const bounds = entry.boundingClientRect || node.getBoundingClientRect();
                const elementHeight = bounds.height;
                const isVisible = entry.intersectionRatio >= this.options.targetVisibleIntersectionThreshold ||
                    (entry.intersectionRatio * elementHeight) / containerHeight >=
                        this.options.containerVisibleIntersectionThreshold;
                // Element is still visible since last check
                if (isVisible && element && element.timeoutSet) {
                    return;
                }
                // If the element is visible
                if (isVisible && element) {
                    // Set the visibility state to true but wait until the timeout finishes
                    // to fire the event
                    element.timeoutSet = true;
                    // Start timer
                    this.timeouts.set(node, setTimeout(() => {
                        // Fire the event
                        this._onVisibilityChange(node, true);
                    }, this.options.minTimeVisible));
                }
                else {
                    const timeout = this.timeouts.get(node);
                    // Fire event
                    this._onVisibilityChange(node, false);
                    // Element is no longer visible, delete timeout
                    clearTimeout(timeout);
                    this.timeouts.delete(node);
                    if (element) {
                        element.timeoutSet = false;
                    }
                }
            });
        };
        /**
         * callback for orientationchange or other event which may cause element visible change
         *
         * Fire onChange event listeners for all visible elements
         */
        this._reportVisibilityStates = () => {
            this.elements.forEach(({ visible }, element) => {
                if (visible) {
                    this._onVisibilityChange(element, visible);
                }
            });
        };
        this.options = options;
        this.elements = new Map();
        this.timeouts = new Map();
        this.observer = new IntersectionObserver(this._watchElements, {
            root: this.options.root,
            rootMargin: this.options.rootMargin,
            threshold: this.options.threshold,
        });
        this._addEventListeners();
    }
    destroy() {
        this.elements.clear();
        this.timeouts.clear();
        if (typeof this.observer.disconnect === 'function') {
            this.observer.disconnect();
        }
        this._removeEventListeners();
    }
    /**
     * Handle DOM element visibility state change
     * @param  {Element} element - the DOM node
     * @param  {Boolean} visible - the visibility state of the entry
     */
    _onVisibilityChange(element, visible) {
        const entry = this.elements.get(element);
        if (!entry) {
            return;
        }
        // Set the new visibility state
        entry.visible = visible;
        const { callback } = entry;
        callback(visible);
    }
    _addEventListeners() {
        window.addEventListener('focus', this._reportVisibilityStates);
        window.addEventListener('orientationchange', this._reportVisibilityStates);
    }
    _removeEventListeners() {
        window.removeEventListener('focus', this._reportVisibilityStates);
        window.removeEventListener('orientationchange', this._reportVisibilityStates);
    }
}
exports.ViewObserver = ViewObserver;
ViewObserver.Factory = {
    instances: new Map(),
    get: (options) => {
        const { scene } = options;
        let instance = ViewObserver.Factory.instances.get(scene);
        if (!instance) {
            instance = new ViewObserver(options);
            ViewObserver.Factory.instances.set(scene, instance);
        }
        return instance;
    },
};
